import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";

interface PopupContextProps {
  popups: Record<string, boolean>;
  openPopup: (popupType: string, persistent?: boolean) => void;
  closePopup: (popupType: string) => void;
}

const PopupContext = createContext<PopupContextProps | undefined>(undefined);

export const PopupProvider = ({ children }: { children: ReactNode }) => {
  // const [popups, setPopups] = useState<Record<string, boolean>>({});
  const [popups, setPopups] = useState<Record<string, boolean>>(() => {
    const storedPopups = localStorage.getItem("popups");
    return storedPopups ? JSON.parse(storedPopups) : {};
  });
  const [persistentPopups, setPersistentPopups] = useState<Set<string>>(() => {
    const storedPersistent = localStorage.getItem("persistentPopups");
    return storedPersistent ? new Set(JSON.parse(storedPersistent)) : new Set();
  });


  // Save persistent popups to localStorage when `popups` or `persistentPopups` changes
  useEffect(() => {
    const persistentStates: Record<string, boolean> = {};
    persistentPopups.forEach((popupType) => {
      if (popups[popupType]) {
        persistentStates[popupType] = true;
      }
    });
    localStorage.setItem("popups", JSON.stringify(persistentStates));
    localStorage.setItem(
      "persistentPopups",
      JSON.stringify(Array.from(persistentPopups))
    );
  }, [popups, persistentPopups]);

  const openPopup = (popupType: string, persistent: boolean = true) => {
    setPopups((prevPopups) => ({
      ...prevPopups,
      [popupType]: true,
    }));

    if (persistent) {
      setPersistentPopups((prev) => {
        const updated = new Set(prev);
        updated.add(popupType);
        return updated;
      });
    }
  };

  const closePopup = (popupType: string) => {
    setPopups((prevPopups) => ({
      ...prevPopups,
      [popupType]: false,
    }));

    setPersistentPopups((prev) => {
      const updated = new Set(prev);
      updated.delete(popupType);
      return updated;
    });
  };

  return (
    <PopupContext.Provider value={{ popups, openPopup, closePopup }}>
      {children}
    </PopupContext.Provider>
  );
};

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error("usePopup must be used within a PopupProvider");
  }
  return context;
};
